import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Paragraph, Packer, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// ── Accent color (Microsoft Word blue) ──
const ACCENT = '#185ABD';

// ── Data helpers ──
let _blockId = 0;
function createBlock(overrides = {}) {
  _blockId++;
  return {
    id: _blockId,
    text: '',
    fontSize: 14,
    color: '#000000',
    align: 'left',
    bold: false,
    italic: false,
    underline: false,
    fontFamily: 'Helvetica',
    ...overrides,
  };
}

function createPage() {
  return { id: Date.now(), blocks: [createBlock()] };
}

// ── Colors for picker ──
const COLORS = [
  '#000000','#333333','#888888','#185ABD',
  '#2196F3','#3F51B5','#F44336','#C00000',
  '#FF9800','#4CAF50','#006600','#009688',
  '#9C27B0','#795548','#E91E63','#00BCD4',
];

const FONTS = ['Helvetica', 'Times', 'Courier'];

export default function CompletePDFEditor() {
  const [pages, setPages] = useState([createPage()]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [activeBlockIndex, setActiveBlockIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Ribbon state
  const [fontSize, setFontSize] = useState(14);
  const [color, setColor] = useState('#000000');
  const [align, setAlign] = useState('left');
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [fontFamily, setFontFamily] = useState('Helvetica');

  const blockRefs = useRef({});
  const colorPickerRef = useRef(null);
  const exportMenuRef = useRef(null);

  const currentPage = pages[currentPageIndex];

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Has any content ──
  const hasContent = pages.some(p => p.blocks.some(b => b.text.trim() !== ''));

  // ── Focus a block ──
  const focusBlock = useCallback((pageIdx, index) => {
    const pi = pageIdx ?? currentPageIndex;
    setActiveBlockIndex(index);
    const block = pages[pi]?.blocks[index];
    if (block) {
      setFontSize(block.fontSize);
      setColor(block.color);
      setAlign(block.align);
      setBold(block.bold);
      setItalic(block.italic);
      setUnderline(block.underline);
      setFontFamily(block.fontFamily);
    }
    setTimeout(() => {
      const el = blockRefs.current[`${pi}-${index}`];
      if (el) el.focus();
    }, 0);
  }, [pages, currentPageIndex]);

  // ── Apply ribbon style to active block ──
  const applyStyle = useCallback((updates = {}) => {
    if (activeBlockIndex == null) return;
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const block = next[currentPageIndex]?.blocks[activeBlockIndex];
      if (!block) return prev;
      Object.assign(block, {
        fontSize: updates.fontSize ?? fontSize,
        color: updates.color ?? color,
        align: updates.align ?? align,
        bold: updates.bold ?? bold,
        italic: updates.italic ?? italic,
        underline: updates.underline ?? underline,
        fontFamily: updates.fontFamily ?? fontFamily,
      });
      return next;
    });
  }, [activeBlockIndex, currentPageIndex, fontSize, color, align, bold, italic, underline, fontFamily]);

  // ── Block text change ──
  const handleTextChange = (index, value) => {
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next[currentPageIndex].blocks[index].text = value;
      return next;
    });
  };

  // ── Add new block ──
  const addNewBlock = useCallback((afterIndex) => {
    const newBlock = createBlock({ fontSize, color, align, bold, italic, underline, fontFamily });
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const insertAt = afterIndex != null ? afterIndex + 1 : next[currentPageIndex].blocks.length;
      next[currentPageIndex].blocks.splice(insertAt, 0, newBlock);
      return next;
    });
    const newIndex = afterIndex != null ? afterIndex + 1 : pages[currentPageIndex].blocks.length;
    setTimeout(() => focusBlock(null, newIndex), 0);
  }, [currentPageIndex, fontSize, color, align, bold, italic, underline, fontFamily, pages, focusBlock]);

  // ── Delete block ──
  const deleteBlock = (index) => {
    if (currentPage.blocks.length <= 1) return;
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next[currentPageIndex].blocks.splice(index, 1);
      return next;
    });
    setActiveBlockIndex(prev => {
      if (prev === index) return Math.max(0, index - 1);
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  // ── Page management ──
  const addPage = () => {
    const p = createPage();
    setPages(prev => [...prev, p]);
    const newIdx = pages.length;
    setCurrentPageIndex(newIdx);
    setActiveBlockIndex(0);
    setTimeout(() => focusBlock(newIdx, 0), 50);
  };

  const deletePage = (index) => {
    if (pages.length <= 1) return;
    setPages(prev => prev.filter((_, i) => i !== index));
    setCurrentPageIndex(prev => Math.max(0, prev - 1));
    setActiveBlockIndex(0);
  };

  // ── Canvas click — add block at bottom ──
  const handleCanvasClick = (e) => {
    if (e.target === e.currentTarget || e.target.dataset.canvas) {
      addNewBlock(currentPage.blocks.length - 1);
    }
  };

  // ── Enter key — add new block below ──
  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addNewBlock(index);
    }
    if (e.key === 'Backspace' && currentPage.blocks[index]?.text === '' && currentPage.blocks.length > 1) {
      e.preventDefault();
      deleteBlock(index);
    }
  };

  // ═══════════════════════════════
  //  EXPORT — PDF
  // ═══════════════════════════════
  const exportPDF = async () => {
    setSaving(true);
    try {
      const pdfDoc = await PDFDocument.create();
      const fontHelvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontHelveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontTimes = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const fontTimesBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
      const fontCourier = await pdfDoc.embedFont(StandardFonts.Courier);
      const fontCourierBold = await pdfDoc.embedFont(StandardFonts.CourierBold);

      const pickFont = (fam, isBold) => {
        if (fam === 'Courier') return isBold ? fontCourierBold : fontCourier;
        if (fam === 'Times') return isBold ? fontTimesBold : fontTimes;
        return isBold ? fontHelveticaBold : fontHelvetica;
      };

      const pageW = 612, pageH = 792, margin = 50;
      const maxW = pageW - margin * 2;

      for (const page of pages) {
        const pdfPage = pdfDoc.addPage([pageW, pageH]);
        let y = pageH - margin;

        for (const block of page.blocks) {
          const text = block.text;
          if (!text.trim()) continue;
          const font = pickFont(block.fontFamily, block.bold);
          const fs = block.fontSize;
          const c = block.color || '#000000';
          const r = parseInt(c.slice(1, 3), 16) / 255;
          const g = parseInt(c.slice(3, 5), 16) / 255;
          const b = parseInt(c.slice(5, 7), 16) / 255;

          const paragraphs = text.split('\n');
          for (const para of paragraphs) {
            if (!para.trim()) { y -= fs + 4; continue; }
            const words = para.split(' ');
            let line = '';
            const lines = [];
            for (const w of words) {
              const test = line ? `${line} ${w}` : w;
              if (font.widthOfTextAtSize(test, fs) > maxW && line) {
                lines.push(line);
                line = w;
              } else line = test;
            }
            if (line) lines.push(line);

            for (const ln of lines) {
              let x = margin;
              const tw = font.widthOfTextAtSize(ln, fs);
              if (block.align === 'center') x = margin + (maxW - tw) / 2;
              else if (block.align === 'right') x = margin + maxW - tw;
              pdfPage.drawText(ln, { x, y, size: fs, font, color: rgb(r, g, b) });
              y -= fs + 4;
            }
          }
          y -= 6;
        }
      }

      const bytes = await pdfDoc.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      saveAs(blob, 'document.pdf');
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setSaving(false);
      setShowExportMenu(false);
    }
  };

  // ═══════════════════════════════
  //  EXPORT — DOCX
  // ═══════════════════════════════
  const exportWord = async () => {
    setSaving(true);
    try {
      const sections = [];
      for (const page of pages) {
        const children = [];
        for (const block of page.blocks) {
          if (!block.text.trim()) continue;
          let alignment = 'left';
          if (block.align === 'center') alignment = 'center';
          if (block.align === 'right') alignment = 'end';

          let fontName = 'Calibri';
          if (block.fontFamily === 'Courier') fontName = 'Courier New';
          if (block.fontFamily === 'Times') fontName = 'Times New Roman';

          const lines = block.text.split('\n');
          for (const line of lines) {
            children.push(new Paragraph({
              alignment,
              children: [new TextRun({
                text: line,
                size: block.fontSize * 2,
                color: block.color.replace('#', ''),
                bold: block.bold,
                italics: block.italic,
                underline: block.underline ? {} : undefined,
                font: fontName,
              })],
            }));
          }
        }
        sections.push({ children });
      }

      const doc = new Document({ sections });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, 'document.docx');
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setSaving(false);
      setShowExportMenu(false);
    }
  };

  // Auto-resize textareas on mount/page change
  useEffect(() => {
    Object.values(blockRefs.current).forEach(el => {
      if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
    });
  }, [currentPageIndex, pages]);

  // ═══════════════════════════════
  //  RENDER
  // ═══════════════════════════════
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      fontFamily: "'Segoe UI', Roboto, sans-serif", background: '#e0e0e0',
      position: 'fixed', inset: 0, zIndex: 999,
    }}>
      {/* ── Title Bar ── */}
      <div style={{
        height: 50, background: ACCENT, display: 'flex', alignItems: 'center',
        padding: '0 8px', gap: 8, flexShrink: 0,
      }}>
        <button onClick={() => window.location.reload()} style={iconBtnStyle} title="Back">
          <span style={{ fontSize: 20, color: '#fff' }}>←</span>
        </button>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }}>📄</span>
        <span style={{
          color: '#fff', fontSize: 15, fontWeight: 600, flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          Document - PDF ME
        </span>
        {saving ? (
          <div style={{
            width: 20, height: 20,
            border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 12,
          }} />
        ) : (
          <div style={{ position: 'relative' }} ref={exportMenuRef}>
            <button
              onClick={() => hasContent && setShowExportMenu(v => !v)}
              disabled={!hasContent}
              style={{
                height: 34, padding: '0 14px',
                background: hasContent ? '#fff' : 'rgba(255,255,255,0.38)',
                color: hasContent ? ACCENT : 'rgba(255,255,255,0.54)',
                border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13,
                cursor: hasContent ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              📤 Export
            </button>
            {showExportMenu && (
              <div style={{
                position: 'absolute', right: 0, top: 40, background: '#fff',
                borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
                padding: 8, zIndex: 10, minWidth: 220,
              }}>
                <button onClick={exportPDF} style={exportOptStyle}
                  onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize: 20 }}>📕</span>
                  <span>PDF Document (.pdf)</span>
                </button>
                <button onClick={exportWord}
                  style={{ ...exportOptStyle, borderTop: '1px solid #eee' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize: 20 }}>📘</span>
                  <span>Word Document (.docx)</span>
                </button>
              </div>
            )}
          </div>
        )}
        <div style={{ width: 8 }} />
      </div>

      {/* ── Ribbon Toolbar ── */}
      <div style={{
        background: '#f8f8f8', padding: '4px 8px', display: 'flex',
        alignItems: 'center', gap: 6, overflowX: 'auto', flexShrink: 0,
        borderBottom: '1px solid #ddd',
      }}>
        {/* Font family */}
        <select value={fontFamily}
          onChange={e => { setFontFamily(e.target.value); applyStyle({ fontFamily: e.target.value }); }}
          style={{
            height: 32, border: '1px solid #ccc', borderRadius: 4,
            fontSize: 13, padding: '0 4px', background: '#fff',
          }}>
          {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        {/* Font size spinner */}
        <div style={{
          display: 'flex', alignItems: 'center', border: '1px solid #ccc',
          borderRadius: 4, background: '#fff', height: 32, overflow: 'hidden',
        }}>
          <span style={{ width: 32, textAlign: 'center', fontSize: 13, lineHeight: '32px' }}>
            {fontSize}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', width: 18 }}>
            <button onClick={() => {
              const v = Math.min(96, fontSize + 1); setFontSize(v); applyStyle({ fontSize: v });
            }} style={spinBtnStyle}>▲</button>
            <button onClick={() => {
              const v = Math.max(6, fontSize - 1); setFontSize(v); applyStyle({ fontSize: v });
            }} style={spinBtnStyle}>▼</button>
          </div>
        </div>

        <Divider />

        {/* Bold / Italic / Underline */}
        <RibbonToggle icon="B" active={bold} extraStyle={{ fontWeight: 800 }}
          onClick={() => { const v = !bold; setBold(v); applyStyle({ bold: v }); }} />
        <RibbonToggle icon="I" active={italic} extraStyle={{ fontStyle: 'italic' }}
          onClick={() => { const v = !italic; setItalic(v); applyStyle({ italic: v }); }} />
        <RibbonToggle icon="U" active={underline} extraStyle={{ textDecoration: 'underline' }}
          onClick={() => { const v = !underline; setUnderline(v); applyStyle({ underline: v }); }} />

        <Divider />

        {/* Color picker */}
        <div style={{ position: 'relative' }} ref={colorPickerRef}>
          <button onClick={() => setShowColorPicker(v => !v)}
            style={{
              ...ribbonBtnBase,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 1,
            }}>
            <span style={{ fontSize: 16 }}>A</span>
            <div style={{ width: 18, height: 3, background: color, borderRadius: 1 }} />
          </button>
          {showColorPicker && (
            <div style={{
              position: 'absolute', top: 36, left: 0, background: '#fff',
              borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.16)',
              padding: 12, zIndex: 10, width: 200,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Font Color</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {COLORS.map(c => (
                  <button key={c}
                    onClick={() => { setColor(c); applyStyle({ color: c }); setShowColorPicker(false); }}
                    style={{
                      width: 32, height: 32, background: c,
                      border: color === c ? `2px solid ${ACCENT}` : '1px solid #ccc',
                      borderRadius: 4, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    {color === c && <span style={{ color: '#fff', fontSize: 14, textShadow: '0 0 2px rgba(0,0,0,0.5)' }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <Divider />

        {/* Alignment */}
        <RibbonToggle icon="☰←" active={align === 'left'} title="Align Left"
          onClick={() => { setAlign('left'); applyStyle({ align: 'left' }); }} fs={13} />
        <RibbonToggle icon="☰↔" active={align === 'center'} title="Center"
          onClick={() => { setAlign('center'); applyStyle({ align: 'center' }); }} fs={13} />
        <RibbonToggle icon="☰→" active={align === 'right'} title="Align Right"
          onClick={() => { setAlign('right'); applyStyle({ align: 'right' }); }} fs={13} />
      </div>

      {/* ── Page Tabs ── */}
      <div style={{
        height: 36, background: '#e8e8e8', display: 'flex',
        alignItems: 'center', flexShrink: 0,
      }}>
        <div style={{
          flex: 1, display: 'flex', overflowX: 'auto', padding: '4px 8px', gap: 2,
        }}>
          {pages.map((p, i) => {
            const sel = i === currentPageIndex;
            return (
              <button key={p.id}
                onClick={() => { setCurrentPageIndex(i); setActiveBlockIndex(0); }}
                onDoubleClick={() => { if (pages.length > 1 && window.confirm(`Delete Page ${i + 1}?`)) deletePage(i); }}
                title="Double-click to delete"
                style={{
                  padding: '0 16px', height: 28, border: 'none', cursor: 'pointer',
                  background: sel ? '#fff' : 'transparent',
                  borderTop: sel ? `2px solid ${ACCENT}` : '2px solid transparent',
                  borderLeft: sel ? '1px solid #ccc' : 'none',
                  borderRight: sel ? '1px solid #ccc' : 'none',
                  borderBottom: 'none',
                  fontSize: 12, fontWeight: sel ? 600 : 400,
                  color: sel ? ACCENT : '#666',
                }}>
                Page {i + 1}
              </button>
            );
          })}
        </div>
        <button onClick={addPage}
          style={{
            margin: '0 10px', padding: '6px 20px', background: ACCENT, color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
          }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>+</span> Add Page
        </button>
      </div>

      {/* ── Canvas ── */}
      <div
        style={{
          flex: 1, overflow: 'auto', background: '#e0e0e0',
          display: 'flex', justifyContent: 'center', padding: '16px 24px',
        }}
        onClick={handleCanvasClick}
        data-canvas="true"
      >
        <div
          style={{
            width: '100%', maxWidth: 680, background: '#fff', minHeight: 900,
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)', padding: '48px 48px 200px',
            cursor: 'text',
          }}
          data-canvas="true"
          onClick={handleCanvasClick}
        >
          {currentPage.blocks.map((block, index) => {
            const isActive = activeBlockIndex === index;
            return (
              <div key={block.id} style={{
                marginBottom: 2, padding: '2px 4px', position: 'relative',
                background: isActive ? 'rgba(24,90,189,0.03)' : 'transparent',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <textarea
                    ref={el => { blockRefs.current[`${currentPageIndex}-${index}`] = el; }}
                    value={block.text}
                    onChange={e => handleTextChange(index, e.target.value)}
                    onFocus={() => focusBlock(null, index)}
                    onKeyDown={e => handleKeyDown(e, index)}
                    rows={1}
                    placeholder={index === 0 && currentPage.blocks.length === 1 ? 'Start typing…' : ''}
                    spellCheck={false}
                    style={{
                      flex: 1, border: 'none', outline: 'none', resize: 'none',
                      background: 'transparent',
                      fontSize: block.fontSize, color: block.color, textAlign: block.align,
                      fontWeight: block.bold ? 'bold' : 'normal',
                      fontStyle: block.italic ? 'italic' : 'normal',
                      textDecoration: block.underline ? 'underline' : 'none',
                      fontFamily: block.fontFamily === 'Courier' ? 'Courier New, monospace'
                        : block.fontFamily === 'Times' ? 'Times New Roman, serif' : 'inherit',
                      lineHeight: 1.6, padding: '4px 0', overflow: 'hidden',
                      minHeight: 24,
                    }}
                    onInput={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  />
                  {isActive && currentPage.blocks.length > 1 && (
                    <button onClick={() => deleteBlock(index)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: '4px', color: '#bbb', fontSize: 12, lineHeight: 1,
                      }}
                      title="Delete block">
                      ✕
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Spinner animation */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Small components ──

function Divider() {
  return <div style={{ width: 1, height: 24, background: '#ccc', margin: '0 4px' }} />;
}

function RibbonToggle({ icon, active, onClick, extraStyle = {}, title, fs }) {
  return (
    <button onClick={onClick} title={title}
      style={{
        ...ribbonBtnBase,
        background: active ? 'rgba(24,90,189,0.12)' : 'transparent',
        border: active ? '1px solid rgba(24,90,189,0.3)' : '1px solid transparent',
        color: active ? ACCENT : '#555',
        fontSize: fs || 15,
        ...extraStyle,
      }}>
      {icon}
    </button>
  );
}

const ribbonBtnBase = {
  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 4, cursor: 'pointer', border: 'none', background: 'transparent',
};

const iconBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer', width: 36, height: 36,
  display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4,
};

const spinBtnStyle = {
  border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 8,
  height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#666', padding: 0,
};

const exportOptStyle = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
  background: 'transparent', border: 'none', cursor: 'pointer', width: '100%',
  borderRadius: 6, fontSize: 14, fontWeight: 500, color: '#333',
};
